/**
 * @author        patrick@zumper.com (Patrick Giddings)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   Rent Data bizos (for rent research)
 */

import { Bizo, autoclass } from './base'
import type {
  RawRentRange,
  RawRentalData,
  RawRentalDataHomepage,
  RawRentalDataHood,
  RawRentalDataInfo,
  RawRentalDataLocation,
  RawRentalDataLocationBedPropertyType,
  RawRentalDataLocationBedType,
  RawRentalDataLocationPropertyType,
  RawRentalDataNational,
  RawRentalDataNationalBedPropertyType,
  RawRentalDataNationalBedType,
  RawRentalDataNationalInfo,
  RawRentalDataNationalPropertyType,
  RawUniqueRentRangesGrouped,
  RentRange as RentRangeType,
  RentalDataHomepage as RentalDataHomepageType,
  RentalDataHood as RentalDataHoodType,
  RentalDataInfo as RentalDataInfoType,
  RentalDataLocationBedPropertyType as RentalDataLocationBedPropertyTypeType,
  RentalDataLocationBedType as RentalDataLocationBedTypeType,
  RentalDataLocationPropertyType as RentalDataLocationPropertyTypeType,
  RentalDataLocation as RentalDataLocationType,
  RentalDataNationalBedPropertyType as RentalDataNationalBedPropertyTypeType,
  RentalDataNationalBedType as RentalDataNationalBedTypeType,
  RentalDataNationalInfo as RentalDataNationalInfoType,
  RentalDataNationalPropertyType as RentalDataNationalPropertyTypeType,
  RentalDataNational as RentalDataNationalType,
  RentalData as RentalDataType,
  UniqueRentRangesGrouped as UniqueRentRangesGroupedType,
} from './rentalData.types'

export const RentRange: typeof RentRangeType = autoclass<RentRangeType, RawRentRange>(
  'RentRange',
  class RentRange extends Bizo<RentRangeType, RawRentRange> {},
  {
    maxRentRange: 'max_rent_range',
    minRentRange: 'min_rent_range',
    rentRangeCount: 'rent_range_count',
    rentRangePercentage: 'rent_range_percentage',
  }
)

export type RentRange = RentRangeType

export const UniqueRentRangesGrouped: typeof UniqueRentRangesGroupedType = autoclass<
  UniqueRentRangesGroupedType,
  RawUniqueRentRangesGrouped
>(
  'UniqueRentRangesGrouped',
  class UniqueRentRangesGrouped extends Bizo<
    UniqueRentRangesGroupedType,
    RawUniqueRentRangesGrouped
  > {},
  {
    1: { name: '1', type: RentRange },
    2: { name: '2', type: RentRange },
    3: { name: '3', type: RentRange },
  }
)

export type UniqueRentRangesGrouped = UniqueRentRangesGroupedType

export const RentalDataInfo: typeof RentalDataInfoType = autoclass<
  RentalDataInfoType,
  RawRentalDataInfo
>('RentalDataInfo', class RentalDataInfo extends Bizo<RentalDataInfoType, RawRentalDataInfo> {}, {
  // averageRent: 'average_rent', // averageRent exists, but we should *always* use medianRent instead
  averageSqFt: 'average_sqft',
  count: 'count',
  differenceIncreaseYoy: 'difference_increase_yoy',
  medianRent: 'median_rent',
  monthlyMedianRent: 'monthly_median_rent',
  avgPercentChangeMonth: 'avg_percent_change_month',
  medianPercentChangeMonth: 'median_percent_change_month',
  avgPercentChangeYoy: 'avg_percent_change_yoy',
  medianPercentChangeYoy: 'median_percent_change_yoy',
  rentRanges: 'rent_ranges',
})

export type RentalDataInfo = RentalDataInfoType

export const RentalDataNationalInfo: typeof RentalDataNationalInfoType = autoclass<
  RentalDataNationalInfoType,
  RawRentalDataNationalInfo
>(
  'RentalDataNationalInfo',
  class RentalDataNationalInfo extends Bizo<
    RentalDataNationalInfoType,
    RawRentalDataNationalInfo
  > {},
  {
    // averageRent: 'average_rent', // averageRent exists, but we should *always* use medianRent instead
    medianRent: 'median_rent',
  }
)

export type RentalDataNationalInfo = RentalDataNationalInfoType

export const RentalDataLocationBedType: typeof RentalDataLocationBedTypeType = autoclass<
  RentalDataLocationBedTypeType,
  RawRentalDataLocationBedType
>(
  'RentalDataLocationBedType',
  class RentalDataLocationBedType extends Bizo<
    RentalDataLocationBedTypeType,
    RawRentalDataLocationBedType
  > {},
  {
    oneBeds: {
      name: '1_BED',
      type: RentalDataInfo,
    },
    twoBeds: {
      name: '2_BED',
      type: RentalDataInfo,
    },
    threeBeds: {
      name: '3_BED',
      type: RentalDataInfo,
    },
    fourBeds: {
      name: '4+_BED',
      type: RentalDataInfo,
    },
    studio: {
      name: 'STUDIO',
      type: RentalDataInfo,
    },
  }
)

export type RentalDataLocationBedType = RentalDataLocationPropertyTypeType

export const RentalDataNationalBedType: typeof RentalDataNationalBedTypeType = autoclass<
  RentalDataNationalBedTypeType,
  RawRentalDataNationalBedType
>(
  'RentalDataNationalBedType',
  class RentalDataNationalBedType extends Bizo<
    RentalDataNationalBedTypeType,
    RawRentalDataNationalBedType
  > {},
  {
    oneBeds: {
      name: '1_BED',
      type: RentalDataNationalInfo,
    },
    twoBeds: {
      name: '2_BED',
      type: RentalDataNationalInfo,
    },
    threeBeds: {
      name: '3_BED',
      type: RentalDataNationalInfo,
    },
    fourBeds: {
      name: '4+_BED',
      type: RentalDataNationalInfo,
    },
    studio: {
      name: 'STUDIO',
      type: RentalDataNationalInfo,
    },
  }
)

export type RentalDataNationalBedType = RentalDataNationalBedTypeType

export const RentalDataLocationPropertyType: typeof RentalDataLocationPropertyTypeType = autoclass<
  RentalDataLocationPropertyTypeType,
  RawRentalDataLocationPropertyType
>(
  'RentalDataLocationPropertyType',
  class RentalDataLocationPropertyType extends Bizo<
    RentalDataLocationPropertyTypeType,
    RawRentalDataLocationPropertyType
  > {},
  {
    AFR: {
      name: 'AFR',
      type: RentalDataInfo,
    },
    CFR: {
      name: 'CFR',
      type: RentalDataInfo,
    },
    HFR: {
      name: 'HFR',
      type: RentalDataInfo,
    },
    RFR: {
      name: 'RFR',
      type: RentalDataInfo,
    },
  }
)

export type RentalDataLocationPropertyType = RentalDataLocationPropertyTypeType

export const RentalDataNationalPropertyType: typeof RentalDataNationalPropertyTypeType = autoclass<
  RentalDataNationalPropertyTypeType,
  RawRentalDataNationalPropertyType
>(
  'RentalDataNationalPropertyType',
  class RentalDataNationalPropertyType extends Bizo<
    RentalDataNationalPropertyTypeType,
    RawRentalDataNationalPropertyType
  > {},
  {
    AFR: {
      name: 'AFR',
      type: RentalDataNationalInfo,
    },
    CFR: {
      name: 'CFR',
      type: RentalDataNationalInfo,
    },
    HFR: {
      name: 'HFR',
      type: RentalDataNationalInfo,
    },
    RFR: {
      name: 'RFR',
      type: RentalDataNationalInfo,
    },
  }
)

export type RentalDataNationalPropertyType = RentalDataNationalPropertyTypeType

export const RentalDataLocationBedPropertyType: typeof RentalDataLocationBedPropertyTypeType =
  autoclass<RentalDataLocationBedPropertyTypeType, RawRentalDataLocationBedPropertyType>(
    'RentalDataLocationBedPropertyType',
    class RentalDataLocationBedPropertyType extends Bizo<
      RentalDataLocationBedPropertyTypeType,
      RawRentalDataLocationBedPropertyType
    > {},
    {
      oneBeds: {
        name: '1_BED',
        type: RentalDataLocationPropertyType,
      },
      twoBeds: {
        name: '2_BED',
        type: RentalDataLocationPropertyType,
      },
      threeBeds: {
        name: '3_BED',
        type: RentalDataLocationPropertyType,
      },
      fourBeds: {
        name: '4+_BED',
        type: RentalDataLocationPropertyType,
      },
      studio: {
        name: 'STUDIO',
        type: RentalDataLocationPropertyType,
      },
    }
  )

export type RentalDataLocationBedPropertyType = RentalDataLocationBedPropertyTypeType

export const RentalDataNationalBedPropertyType: typeof RentalDataNationalBedPropertyTypeType =
  autoclass<RentalDataNationalBedPropertyTypeType, RawRentalDataNationalBedPropertyType>(
    'RentalDataNationalBedPropertyType',
    class RentalDataNationalBedPropertyType extends Bizo<
      RentalDataNationalBedPropertyTypeType,
      RawRentalDataNationalBedPropertyType
    > {},
    {
      oneBeds: {
        name: '1_BED',
        type: RentalDataNationalPropertyType,
      },
      twoBeds: {
        name: '2_BED',
        type: RentalDataLocationPropertyType,
      },
      threeBeds: {
        name: '3_BED',
        type: RentalDataLocationPropertyType,
      },
      fourBeds: {
        name: '4+_BED',
        type: RentalDataLocationPropertyType,
      },
      studio: {
        name: 'STUDIO',
        type: RentalDataLocationPropertyType,
      },
    }
  )

export type RentalDataNationalBedPropertyType = RentalDataNationalBedPropertyTypeType

const RentalDataLocationConfig = {
  bed: {
    name: 'bed',
    type: RentalDataLocationBedType,
  },
  bedPropertyType: {
    name: 'bed_property_type',
    type: RentalDataLocationBedPropertyType,
  },
  city: 'city',
  feature: 'feature',
  featureBedPropertyType: 'feature_bed_property_type',
  featurePropertyType: 'feature_property_type',
  hood: 'hood',
  propertyType: {
    name: 'property_type',
    type: RentalDataLocationPropertyType,
  },
  state: 'state',
  total: {
    name: 'total',
    type: RentalDataInfo,
  },
  url: 'url',
  modifiedOn: 'modified_on',
} as const

export const RentalDataLocation: typeof RentalDataLocationType = autoclass<
  RentalDataLocationType,
  RawRentalDataLocation
>(
  'RentalDataLocation',
  class RentalDataLocation extends Bizo<RentalDataLocationType, RawRentalDataLocation> {},
  RentalDataLocationConfig
)

export type RentalDataLocation = RentalDataLocationType

export const RentalDataNational: typeof RentalDataNationalType = autoclass<
  RentalDataNationalType,
  RawRentalDataNational
>(
  'RentalDataNational',
  class RentalDataNational extends Bizo<RentalDataNationalType, RawRentalDataNational> {},
  {
    bed: {
      name: 'bed',
      type: RentalDataNationalBedType,
    },
    bedPropertyType: {
      name: 'bed_property_type',
      type: RentalDataNationalBedPropertyType,
    },
    feature: 'feature',
    featureBedPropertyType: 'feature_bed_property_type',
    featurePropertyType: 'feature_property_type',
    modifiedOn: 'modified_on',
    propertyType: {
      name: 'property_type',
      type: RentalDataNationalPropertyType,
    },
    total: {
      name: 'total',
      type: RentalDataNationalInfo,
    },
  }
)

export type RentalDataNational = RentalDataNationalType

export const RentalDataHood: typeof RentalDataHoodType = autoclass<
  RentalDataHoodType,
  RawRentalDataHood
>(
  'RentalDataHood',
  class RentalDataLocation extends Bizo<RentalDataHoodType, RawRentalDataHood> {},
  RentalDataLocationConfig
)

export type RentalDataHood = RentalDataHoodType

export const RentalData: typeof RentalDataType = autoclass<RentalDataType, RawRentalData>(
  'RentalData',
  class RentalData extends Bizo<RentalDataType, RawRentalData> {},
  {
    data: {
      name: 'data',
      type: RentalDataLocation,
    },
    hoods: {
      isArray: true,
      name: 'hoods',
      type: RentalDataHood,
    },
    national: {
      name: 'national',
      type: RentalDataNational,
    },
    nearby: {
      isArray: true,
      name: 'nearby',
      type: RentalDataLocation,
    },
  }
)

export const RentalDataHomepage: typeof RentalDataHomepageType = autoclass<
  RentalDataHomepageType,
  RawRentalDataHomepage
>(
  'RentalDataHomepage',
  class RentalDataHomepage extends Bizo<RentalDataHomepageType, RawRentalDataHomepage> {},
  {
    cities: {
      isArray: true,
      name: 'cities',
      type: RentalDataLocation,
    },
    hoods: {
      isArray: true,
      name: 'hoods',
      type: RentalDataHood,
    },
  }
)

export type RentalData = RentalDataType
export type RentalDataHomepage = RentalDataHomepageType
